/**
 * HR 评估报告页 —— 一场面试的完整结果浏览。
 *
 * 包含:
 *   1. 候选人 + 岗位概要 + 总分 + 推荐
 *   2. ★ 岗位能力满足度(capabilities 体系,7 维度已退役)
 *   3. AI 评语 + 追问建议 + 红旗
 *   4. 完整对话文字稿(候选人 vs AI)
 *   5. 操作:重新评估
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Avatar } from '../components/Avatar';
import { api, absoluteUrl } from '../api/client';

type FullData = {
  interview: {
    id: string;
    status: string;
    scheduled_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    duration_minutes: number;
    transcript: { role: string; text: string; ts: string }[];
    usage_tokens: Record<string, number>;
    failure_reason: string | null;
    recording_url?: string | null;
    interview_plan?: any;  // D1+D2:面试剧本
    capability_progress?: Record<string, any>;  // 实时能力进度(judge_service 写)
  };
  candidate: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    resume_summary: any;
  };
  job: {
    id: string;
    title: string;
    description: string;
    requirements: string | null;
    interviewer_name?: string;
    interviewer_avatar?: string;
    capabilities?: { name: string; priority: string; description: string; selected: boolean; custom: boolean }[];
  };
  evaluation: null | {
    overall_score: number | null;
    recommendation: string | null;
    dimension_scores: Record<string, number> | null;
    summary: string | null;
    follow_up_questions: string[] | null;
    red_flags: any[] | null;
    capability_satisfaction: Record<string, { satisfied: 'yes' | 'partial' | 'no'; score: number; evidence: string }> | null;
    plan_completion?: Record<string, any> | null;       // D4:逐阶段任务完成度
    interview_quality_audit?: any;                      // D4:面试质量审计 + 反问环节
    created_at: string | null;
  };
};

const RECOMMENDATION_LABEL: Record<string, { label: string; color: string }> = {
  strong_yes: { label: '强烈推荐', color: '#10b981' },
  yes: { label: '推荐', color: '#22c55e' },
  推荐进入下一轮: { label: '推荐进入下一轮', color: '#22c55e' },
  maybe: { label: '可考虑', color: '#f59e0b' },
  考虑: { label: '可考虑', color: '#f59e0b' },
  no: { label: '不推荐', color: '#ef4444' },
};

export default function AdminEvaluation() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<FullData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reEvaluating, setReEvaluating] = useState(false);
  // 创建成功提示(从 query string 拿候选人链接)
  const [showCreatedToast, setShowCreatedToast] = useState(false);
  const [candidateLink, setCandidateLink] = useState<string | null>(null);
  // plan 生成进度提示
  const planPollIntervalRef = useRef<number | null>(null);

  const silentRefresh = async () => {
    if (!id) return;
    try {
      const { data: d } = await api.get(`/api/interviews/${id}/full`);
      setData(d);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    }
  };
  const refresh = async () => {
    if (!id) return;
    try {
      setLoading(true);
      await silentRefresh();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void refresh(); }, [id]);

  // 处理 ?just_created=1 → 显示成功 toast + 自动轮询直到 plan 生成完
  useEffect(() => {
    const justCreated = searchParams.get('just_created');
    const link = searchParams.get('candidate_link');
    if (justCreated === '1') {
      setShowCreatedToast(true);
      if (link) setCandidateLink(decodeURIComponent(link));
      // 5s 后自动隐藏 toast(但保留 candidate_link 在顶部 banner)
      setTimeout(() => setShowCreatedToast(false), 5000);
      // 清理 query string
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 轮询逻辑:
  //   1) plan 还没生成(status=scheduled + 无 plan)→ 4s 一次拉 plan
  //   2) 面试进行中(status ∈ {launching, ongoing})→ 8s 一次拉 capability_progress + transcript
  //   plan 出现 / 面试结束 → 停轮询
  useEffect(() => {
    const plan = data?.interview?.interview_plan;
    const status = data?.interview?.status;
    const needsPlanPolling = !plan && status === 'scheduled';
    const needsLivePolling = status === 'ongoing' || status === 'launching';
    const interval = needsPlanPolling ? 4000 : (needsLivePolling ? 8000 : 0);

    if (interval > 0 && !planPollIntervalRef.current) {
      planPollIntervalRef.current = window.setInterval(() => { void silentRefresh(); }, interval);
    } else if (interval === 0 && planPollIntervalRef.current) {
      window.clearInterval(planPollIntervalRef.current);
      planPollIntervalRef.current = null;
    }
    return () => {
      if (planPollIntervalRef.current) {
        window.clearInterval(planPollIntervalRef.current);
        planPollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.interview?.interview_plan, data?.interview?.status]);

  const triggerReEvaluate = async () => {
    if (!id) return;
    if (!confirm('重新评估?会覆盖已有评估结果。')) return;
    try {
      setReEvaluating(true);
      await api.post(`/api/interviews/${id}/evaluate`);
      // 后台异步任务,等 1.5s 再刷
      await new Promise((res) => setTimeout(res, 1500));
      await refresh();
      alert('评估已重新生成');
    } catch (e: any) {
      alert('评估失败: ' + e.message);
    } finally {
      setReEvaluating(false);
    }
  };

  // 手动粘贴 transcript 触发评估(本地无 webhook 时的兜底)
  const [pastingTranscript, setPastingTranscript] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const submitPastedTranscript = async () => {
    if (!id) return;
    const text = pasteText.trim();
    if (!text) { alert('请先粘贴 transcript 文本'); return; }
    // 解析:每行格式 "AI: xxx" 或 "候选人: xxx"
    const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
    const turns: { role: string; text: string }[] = [];
    for (const line of lines) {
      const m = line.match(/^(?:\[)?(AI|面试官|候选人|user|assistant|interviewer|candidate)(?:\])?\s*[:|::]\s*(.+)$/i);
      if (m) {
        const r = m[1].toLowerCase();
        const role = ['ai','面试官','assistant','interviewer'].includes(r) ? 'AI' : '候选人';
        turns.push({ role, text: m[2].trim() });
      } else {
        // 没匹配到角色前缀,默认归候选人
        turns.push({ role: '候选人', text: line });
      }
    }
    if (turns.length === 0) { alert('解析不出对话,请按 "AI: xxx\\n候选人: xxx" 格式'); return; }
    try {
      setPastingTranscript(true);
      // 逐条 append
      for (const t of turns) {
        await api.post(`/api/interviews/${id}/transcript/append`, t);
      }
      alert(`✅ 已写入 ${turns.length} 条 transcript,正在触发评估...`);
      // 触发评估
      await api.post(`/api/interviews/${id}/evaluate`);
      await new Promise((res) => setTimeout(res, 2000));
      await refresh();
      setPasteText('');
      alert('评估已生成,刷新查看');
    } catch (e: any) {
      alert('失败:' + e.message);
    } finally {
      setPastingTranscript(false);
    }
  };

  // 手动拉起 AI 入会(HR 后台)
  const triggerLaunch = async () => {
    if (!id) return;
    try {
      const r = await api.post(`/api/interviews/${id}/launch`);
      alert(`✅ 已发送 AI 入会指令(状态:${r.data.status})。等候 30-60s 后 AI 会在 RTC 房间出现。如未生效,点击"诊断 AI 状态"查看详情。`);
      await refresh();
    } catch (e: any) {
      alert('拉起失败: ' + e.message);
    }
  };

  // 强制结束面试 + 触发评估(面试卡在 launching/ongoing 时用)
  const triggerForceComplete = async () => {
    if (!id) return;
    if (!confirm('强制结束面试,标记完成 + 触发评估?\n(用于面试已实际结束但状态卡住的情况)')) return;
    try {
      await api.post(`/api/interviews/${id}/stop`);
      // stop 后端已经会触发评估
      alert('✅ 已结束并触发评估,等 30-60s 刷新查看结果');
      await refresh();
    } catch (e: any) {
      alert('结束失败: ' + e.message);
    }
  };

  const checkAgentStatus = async () => {
    if (!id) return;
    try {
      const r = await api.get(`/api/interviews/${id}/agent-status`);
      const d = r.data;
      const credsOk = (d.missing_credentials?.length ?? 0) === 0;
      const lines = [
        `面试状态:${d.interview_status}`,
        `RTC 任务 ID:${d.rtc_task_id || '(未拉起)'}`,
        `凭据完整性:${credsOk ? '✅ 齐全' : `❌ 缺 ${d.missing_credentials.join(', ')}`}`,
        `字幕回调:${d.callback_url_configured ? '✅ 已配' : '⚠️ 未配 PUBLIC_CALLBACK_BASE_URL(本地无 transcript 是正常的)'}`,
        `Transcript:${d.transcript_total_turns} 句(AI ${d.transcript_ai_turns} 句)`,
        '',
        '【诊断结论】',
        d.diagnostic,
      ];
      alert(lines.join('\n'));
    } catch (e: any) {
      alert('诊断失败: ' + e.message);
    }
  };

  if (loading) return <div className="container"><p className="hint">加载中...</p></div>;
  if (error) return <div className="container"><div className="banner error">{error}</div></div>;
  if (!data) return null;

  const { interview, candidate, job, evaluation } = data;
  const rec = evaluation?.recommendation
    ? RECOMMENDATION_LABEL[evaluation.recommendation] || { label: evaluation.recommendation, color: 'var(--text-muted)' }
    : null;
  const score = evaluation?.overall_score;
  const scoreColor = score == null
    ? 'var(--text-dim)'
    : score >= 8 ? 'var(--success)'
    : score >= 6 ? 'var(--warn)'
    : 'var(--danger)';

  return (
    <div className="container" style={{ paddingBottom: 60 }}>
      {/* 创建成功 toast(5 秒自动消失) */}
      {showCreatedToast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: 'var(--success)', color: 'white',
          padding: '12px 18px', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: 14, fontWeight: 500,
        }}>
          ✅ 面试已创建,候选人链接已复制到剪贴板
        </div>
      )}

      {/* 候选人链接横幅(创建后留着方便复制) */}
      {candidateLink && (
        <div className="card" style={{
          background: 'var(--accent-soft)',
          border: '1px solid var(--accent)',
          marginBottom: 12,
        }}>
          <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="hint" style={{ fontSize: 12, marginBottom: 4 }}>📎 候选人面试链接(请发给候选人)</div>
              <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{candidateLink}</code>
            </div>
            <button onClick={() => {
              navigator.clipboard.writeText(candidateLink);
              alert('已复制');
            }}>📋 再次复制</button>
            <button className="ghost" onClick={() => setCandidateLink(null)}>×</button>
          </div>
        </div>
      )}

      {/* plan 还在生成时显示进度 */}
      {!interview.interview_plan && interview.status === 'scheduled' && (
        <div className="card" style={{
          background: 'var(--bg-elev)',
          borderLeft: '3px solid var(--accent)',
        }}>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            <div style={{ fontSize: 24 }}>⏳</div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>面试剧本生成中</div>
              <div className="hint" style={{ fontSize: 13 }}>
                AI 正在基于岗位深度报告 + 简历筛选诊断生成 4 阶段面试剧本(60-90 秒),
                生成完成后会自动刷新页面。期间候选人链接已可用,但建议等剧本生成完再发起面试。
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="title">{candidate.name || '面试报告'}</h1>
          <div className="subtitle">
            {job.title}
            <span className={`tag ${interview.status}`} style={{ marginLeft: 10 }}>
              {statusLabel(interview.status)}
            </span>
          </div>
        </div>
        <div className="actions">
          <button className="ghost" onClick={() => nav('/admin/interviews')}>← 返回列表</button>
          {/* 拉起 AI 入会(scheduled 状态显示) */}
          {interview.status === 'scheduled' && (
            <button onClick={triggerLaunch} title="立即让 AI 进 RTC 房间(无需等到预约时间)">
              ▶ 立即拉起 AI
            </button>
          )}
          {/* 诊断 AI 状态(launching/ongoing 显示) */}
          {['launching', 'ongoing'].includes(interview.status) && (
            <>
              <button className="secondary" onClick={checkAgentStatus} title="查询 AI 是否真的进了 RTC 房间">
                🔍 诊断
              </button>
              <button className="danger" onClick={triggerForceComplete} title="强制结束面试 + 立即触发评估">
                ⏹ 结束 + 评估
              </button>
            </>
          )}
          {interview.transcript.length > 0 && (
            <button className="secondary" onClick={triggerReEvaluate} disabled={reEvaluating}>
              {reEvaluating ? '评估中...' : '重新评估'}
            </button>
          )}
        </div>
      </div>

      {/* 头部:候选人 + 岗位 + 总分 */}
      <div className="card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <div>
          <div className="hint">候选人</div>
          <h2 style={{ margin: '4px 0' }}>{candidate.name || '(未命名)'}</h2>
          <div className="hint" style={{ fontSize: 13 }}>
            {candidate.email}<br />{candidate.phone}
          </div>
        </div>
        <div>
          <div className="hint">应聘岗位 / 面试官</div>
          <h2 style={{ margin: '4px 0' }}>{job.title}</h2>
          <div className="row" style={{ gap: 6, fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            <Avatar value={job.interviewer_avatar} name={job.interviewer_name} size={20} />
            {job.interviewer_name || '李先生'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="hint">综合评分</div>
          <div style={{ fontSize: 56, fontWeight: 700, color: scoreColor, lineHeight: 1 }}>
            {score == null ? '—' : score.toFixed(1)}
          </div>
          {rec && (
            <div style={{
              display: 'inline-block', marginTop: 8, padding: '4px 12px', borderRadius: 4,
              background: rec.color, color: '#fff', fontSize: 14, fontWeight: 600,
            }}>
              {rec.label}
            </div>
          )}
        </div>
      </div>

      {/* 没有评估 */}
      {!evaluation && (
        <div className="card">
          <h2>⚠️ 本场面试还没有评估</h2>
          {interview.transcript.length === 0 ? (
            <>
              <div className="banner warn" style={{ marginBottom: 12 }}>
                <strong>原因:transcript 为空</strong> — 候选人前端没把字幕推上来。常见原因:
                <ol style={{ margin: '6px 0 0 20px' }}>
                  <li>候选人浏览器加载的是旧版前端代码(Vite hot-reload 偶尔会不生效)→ 强刷候选人页面(Cmd+Shift+R / Ctrl+Shift+R)再发起一场新面试</li>
                  <li>候选人没有点「结束并退出」,直接关掉浏览器 tab → 录音上传被中断,字幕在多数情况已落库,但可能丢尾段</li>
                  <li>面试时间太短(&lt; 10s)或候选人始终没说话 → 豆包 ASR 没识别出文本</li>
                </ol>
              </div>
              <p className="hint" style={{ marginBottom: 12 }}>
                <strong>立即兜底:手动粘 transcript</strong> — 把本场实际对话粘到下面 → 点「提交并评估」。
              </p>
              <textarea
                rows={10}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={`格式举例:\nAI: 你好,先做个自我介绍\n候选人: 我叫孙鹏,10 年 Java 后端 ...\nAI: 你做过 Agent 项目吗?\n候选人: 没做过,但学过 LangChain\n...`}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
              />
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <button onClick={submitPastedTranscript} disabled={pastingTranscript}>
                  {pastingTranscript ? '处理中...' : '✓ 提交并评估'}
                </button>
                <span className="hint" style={{ fontSize: 12 }}>
                  自动按 "AI: xxx" / "候选人: xxx" 解析,提交后立即触发评估
                </span>
              </div>
            </>
          ) : (
            <>
              <p className="hint">已有 {interview.transcript.length} 条 transcript,但未生成评估。</p>
              <button onClick={triggerReEvaluate} disabled={reEvaluating}>
                {reEvaluating ? '评估中...' : '▶ 立即评估'}
              </button>
            </>
          )}
        </div>
      )}

      {/* "★ 岗位能力满足度"是 capabilities 体系下的唯一评分视图,7 维度块已退役 */}
      {evaluation?.capability_satisfaction && Object.keys(evaluation.capability_satisfaction).length > 0 && (
        <div className="card">
          <h2>★ 岗位能力满足度</h2>
          <div className="hint" style={{ marginBottom: 12 }}>
            按岗位创建时设定的能力清单逐条评估,每条引用候选人原话作证据
          </div>
          <div className="col" style={{ gap: 8 }}>
            {(job.capabilities || []).filter((c) => c.selected).map((c) => {
              const sat = evaluation.capability_satisfaction?.[c.name];
              const status = sat?.satisfied || 'no';
              const colors = {
                yes:     { bg: 'var(--success-soft)', border: 'var(--success)', label: '✅ 满足',   text: '#047857' },
                partial: { bg: 'var(--warn-soft)',    border: 'var(--warn)',    label: '⚠️ 部分',   text: '#b45309' },
                no:      { bg: 'var(--danger-soft)',  border: 'var(--danger)',  label: '❌ 未满足', text: '#b91c1c' },
              }[status];
              return (
                <div key={c.name} style={{
                  padding: 12,
                  background: colors.bg,
                  borderLeft: `3px solid ${colors.border}`,
                  borderRadius: 6,
                }}>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div className="row" style={{ gap: 8, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                        {c.priority === 'must_have' && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>★ 必备</span>
                        )}
                      </div>
                      {sat?.evidence && (
                        <div className="hint" style={{ fontSize: 13, fontStyle: 'italic' }}>
                          “{sat.evidence}”
                        </div>
                      )}
                      {!sat && (
                        <div className="hint" style={{ fontSize: 12 }}>(评估器未给出此条数据)</div>
                      )}
                    </div>
                    <span style={{ color: colors.text, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {colors.label}
                      {sat && <span style={{ fontSize: 12, marginLeft: 6, opacity: 0.8 }}>{sat.score?.toFixed(1)}</span>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI 评语 */}
      {evaluation?.summary && (
        <div className="card">
          <h2>AI 综合评语</h2>
          <p style={{ lineHeight: 1.7, margin: 0 }}>{evaluation.summary}</p>
        </div>
      )}

      {/* 追问 + 红旗 */}
      {(evaluation?.follow_up_questions?.length || evaluation?.red_flags?.length) && (
        <div className="row" style={{ gap: 16, alignItems: 'stretch' }}>
          {(evaluation.follow_up_questions?.length ?? 0) > 0 && (
            <div className="card" style={{ flex: 1 }}>
              <h2>建议二面追问</h2>
              <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                {evaluation.follow_up_questions!.map((q, i) => <li key={i}>{q}</li>)}
              </ol>
            </div>
          )}
          {(evaluation.red_flags?.length ?? 0) > 0 && (
            <div className="card" style={{ flex: 1, borderLeft: '4px solid #ef4444' }}>
              <h2 style={{ color: '#ef4444' }}>⚠️ 红旗信号</h2>
              <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                {evaluation.red_flags!.map((f, i) => (
                  <li key={i}>{typeof f === 'string' ? f : JSON.stringify(f)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* 完整文字稿 */}
      <div className="card">
        <h2>完整对话({interview.transcript.length} 句)</h2>
        {interview.transcript.length === 0 ? (
          <p className="hint">暂无文字稿</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 540, overflowY: 'auto' }}>
            {interview.transcript.map((t, i) => {
              const isAI = t.role === 'AI' || t.role === 'interviewer' || t.role === 'agent';
              return (
                <div key={i} style={{
                  display: 'flex',
                  flexDirection: isAI ? 'row' : 'row-reverse',
                  gap: 10,
                  alignItems: 'flex-start',
                }}>
                  <div style={{
                    minWidth: 64, fontSize: 12, color: isAI ? 'var(--accent)' : 'var(--success)',
                    fontWeight: 600, lineHeight: '22px',
                  }}>
                    {isAI ? '🤖 AI' : '👤 候选人'}
                  </div>
                  <div style={{
                    background: isAI ? 'var(--accent-soft)' : 'var(--success-soft)',
                    color: 'var(--text)',
                    border: `1px solid ${isAI ? '#bfdbfe' : '#a7f3d0'}`,
                    padding: '10px 14px', borderRadius: 10, maxWidth: '72%',
                    lineHeight: 1.6, fontSize: 14,
                  }}>
                    {t.text}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 录音回放 */}
      {interview.recording_url && (
        <div className="card">
          <h2>面试录音</h2>
          <audio controls src={absoluteUrl(interview.recording_url)} style={{ width: '100%' }} />
          <div className="hint" style={{ marginTop: 6, fontSize: 12 }}>
            <a href={absoluteUrl(interview.recording_url)} target="_blank" rel="noreferrer">下载录音文件</a>
          </div>
        </div>
      )}

      {/* 面试剧本(interview_plan) */}
      {interview.interview_plan && (
        <details className="card">
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
            🎬 面试剧本 · plan
            {interview.interview_plan?.candidate_brief?.tier && (
              <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                tier={interview.interview_plan.candidate_brief.tier}
                {interview.interview_plan.candidate_brief.job_type && ` · type=${interview.interview_plan.candidate_brief.job_type}`}
                {interview.interview_plan.candidate_brief.level && ` · ${interview.interview_plan.candidate_brief.level}`}
              </span>
            )}
          </summary>
          <div style={{ marginTop: 10 }}>
            {interview.interview_plan?.candidate_brief?.main_thesis && (
              <div style={{ marginBottom: 12, padding: 10, background: 'var(--accent-soft)', borderRadius: 6, fontSize: 13 }}>
                <strong>主战略:</strong>{interview.interview_plan.candidate_brief.main_thesis}
              </div>
            )}
            {interview.interview_plan?.candidate_brief?.stage_combo_reasoning && (
              <div style={{ marginBottom: 12, padding: 10, background: 'var(--bg-elev)', borderRadius: 6, fontSize: 13 }}>
                <strong>阶段组合理由:</strong>{interview.interview_plan.candidate_brief.stage_combo_reasoning}
              </div>
            )}
            <div className="col" style={{ gap: 8 }}>
              {(interview.interview_plan.stages || []).map((s: any, i: number) => (
                <div key={i} style={{ padding: 10, background: 'var(--bg-elev)', borderRadius: 6, fontSize: 13 }}>
                  <div style={{ fontWeight: 600 }}>
                    {s.id} · {s.name} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({s.menu_code} · {s.duration_min} min)</span>
                  </div>
                  {s.agent_persona && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>人格:{s.agent_persona}</div>}
                  {(s.focus_capabilities?.length > 0) && (
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                      <span style={{ color: 'var(--text-muted)' }}>验证能力:</span>
                      {s.focus_capabilities.map((c: string, ci: number) => {
                        const prog = interview.capability_progress?.[c];
                        const status = prog?.status || 'pending';
                        const color = status === 'yes' ? 'var(--success)' :
                                      status === 'partial' ? '#f59e0b' :
                                      status === 'no' ? 'var(--danger)' : 'var(--text-muted)';
                        return (
                          <span key={ci} style={{ marginLeft: 6, padding: '1px 6px', background: color, color: 'white', borderRadius: 3, fontSize: 11 }}>
                            {c} · {status}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {s.anchor_question && <div style={{ marginTop: 4 }}>锚问题:{s.anchor_question}</div>}
                  {s.scenario?.context && <div style={{ marginTop: 4 }}>场景:{s.scenario.context.slice(0, 200)}{s.scenario.context.length > 200 ? '...' : ''}</div>}
                  {s.must_ask_at_end && <div style={{ marginTop: 4, color: 'var(--accent)' }}>★ 收尾必问:{s.must_ask_at_end}</div>}
                </div>
              ))}
            </div>
          </div>
        </details>
      )}

      {/* 实时能力进度(judge_service 写入,面试中也能看) */}
      {interview.capability_progress && Object.keys(interview.capability_progress).filter(k => !k.startsWith('_')).length > 0 && (
        <div className="card">
          <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>🎯 实时能力进度</h2>
            {interview.capability_progress?._stage_progress && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                当前 {interview.capability_progress._stage_progress.stage_id} ·
                must_have {interview.capability_progress._stage_progress.must_have_done_count ?? '?'} / {interview.capability_progress._stage_progress.must_have_total ?? '?'}
                {interview.capability_progress._stage_progress.ready_to_advance && (
                  <span style={{ marginLeft: 8, color: 'var(--success)', fontWeight: 600 }}>✓ ready_to_advance</span>
                )}
              </span>
            )}
          </div>

          {/* 5 strategies × yes/partial/no/pending 计数条 */}
          {(() => {
            const items = Object.entries(interview.capability_progress || {})
              .filter(([k]) => !k.startsWith('_'))
              .map(([name, p]: [string, any]) => ({ name, ...p }));
            const counts = items.reduce((acc: any, it: any) => {
              acc[it.status] = (acc[it.status] || 0) + 1;
              return acc;
            }, {});
            return (
              <div className="row" style={{ gap: 12, marginTop: 12, fontSize: 12 }}>
                <span style={{ padding: '3px 8px', background: 'var(--success)', color: 'white', borderRadius: 3, fontWeight: 600 }}>yes × {counts.yes || 0}</span>
                <span style={{ padding: '3px 8px', background: '#f59e0b', color: 'white', borderRadius: 3, fontWeight: 600 }}>partial × {counts.partial || 0}</span>
                <span style={{ padding: '3px 8px', background: 'var(--danger)', color: 'white', borderRadius: 3, fontWeight: 600 }}>no × {counts.no || 0}</span>
                <span style={{ padding: '3px 8px', background: 'var(--bg-elev)', color: 'var(--text-muted)', borderRadius: 3 }}>pending × {counts.pending || 0}</span>
              </div>
            );
          })()}

          <div className="col" style={{ gap: 8, marginTop: 14 }}>
            {Object.entries(interview.capability_progress || {})
              .filter(([k]) => !k.startsWith('_'))
              .map(([name, p]: [string, any]) => {
                const color = p.status === 'yes' ? 'var(--success)' :
                              p.status === 'partial' ? '#f59e0b' :
                              p.status === 'no' ? 'var(--danger)' : 'var(--text-muted)';
                return (
                  <div key={name} style={{ padding: 10, background: 'var(--bg-elev)', borderRadius: 6, fontSize: 13 }}>
                    <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <strong>{name}</strong>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 3, background: color, color: 'white', fontWeight: 600 }}>{p.status}</span>
                      {p.stage_id && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.stage_id}</span>}
                      {p.attempts !== undefined && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>追问 {p.attempts}</span>}
                      {p.depth_reached !== undefined && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>深度 {p.depth_reached}</span>}
                    </div>
                    {(p.yes_signals_hit?.length > 0) && (
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--success)' }}>✓ {p.yes_signals_hit.slice(0, 3).join(' · ')}</div>
                    )}
                    {(p.no_signals_hit?.length > 0) && (
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--danger)' }}>✗ {p.no_signals_hit.slice(0, 2).join(' · ')}</div>
                    )}
                    {(p.evidence_quotes?.length > 0) && (
                      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>"{p.evidence_quotes[p.evidence_quotes.length - 1]}"</div>
                    )}
                    {p.rationale && (
                      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>{p.rationale}</div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* plan_completion + interview_quality_audit(D4) */}
      {evaluation && (evaluation.plan_completion || evaluation.interview_quality_audit) && (
        <div className="card">
          <h2>📊 计划对照评估(D4)</h2>
          {evaluation.plan_completion && (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>逐阶段任务完成度</h3>
              <div className="col" style={{ gap: 8 }}>
                {Object.entries(evaluation.plan_completion).map(([sid, info]: [string, any]) => {
                  const completed = info?.completed || '?';
                  const color = completed === 'yes' ? 'var(--success)' :
                                completed === 'partial' ? '#f59e0b' :
                                completed === 'no' ? 'var(--danger)' : 'var(--text-muted)';
                  return (
                    <div key={sid} style={{ padding: 10, background: 'var(--bg-elev)', borderRadius: 6, fontSize: 13 }}>
                      <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                        <strong>{sid}</strong>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 3,
                          background: color, color: 'white', fontWeight: 600,
                        }}>{completed}</span>
                        {info?.depth_reached !== undefined && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>追问深度 {info.depth_reached}/3</span>
                        )}
                      </div>
                      {info?.verdict && <div style={{ marginTop: 4 }}>{info.verdict}</div>}
                      {info?.evidence && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>证据:{info.evidence}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {evaluation.interview_quality_audit && (
            <div>
              <h3 style={{ fontSize: 14, marginBottom: 8 }}>面试质量审计</h3>
              <div style={{ padding: 10, background: 'var(--bg-elev)', borderRadius: 6, fontSize: 13 }}>
                {evaluation.interview_quality_audit.tasks_attempted !== undefined && (
                  <div>
                    完成度:{evaluation.interview_quality_audit.tasks_with_sufficient_evidence}
                    {' / '}{evaluation.interview_quality_audit.tasks_attempted} 个任务有足够证据
                  </div>
                )}
                {evaluation.interview_quality_audit.missed_probes?.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <strong>AI 漏问的关键追问:</strong>
                    <ul style={{ margin: '4px 0 0 16px' }}>
                      {evaluation.interview_quality_audit.missed_probes.map((p: string, i: number) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}
                {evaluation.interview_quality_audit.credibility_check_result && (
                  <div style={{ marginTop: 6 }}>
                    <strong>简历宣称交叉验证:</strong>{evaluation.interview_quality_audit.credibility_check_result}
                  </div>
                )}
                {evaluation.interview_quality_audit.candidate_reverse_questions && (
                  <div style={{ marginTop: 10, padding: 8, background: 'var(--accent-soft)', borderRadius: 4 }}>
                    <strong>★ 反问环节(装不出来的最强信号)</strong>
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                      候选人反问:{evaluation.interview_quality_audit.candidate_reverse_questions.asked || '?'} ·
                      深度:{evaluation.interview_quality_audit.candidate_reverse_questions.depth_signal || '?'}
                    </div>
                    {evaluation.interview_quality_audit.candidate_reverse_questions.questions?.length > 0 && (
                      <ul style={{ margin: '4px 0 0 16px', fontSize: 12 }}>
                        {evaluation.interview_quality_audit.candidate_reverse_questions.questions.map((q: string, i: number) => <li key={i}>{q}</li>)}
                      </ul>
                    )}
                    {evaluation.interview_quality_audit.candidate_reverse_questions.what_it_reveals && (
                      <div style={{ marginTop: 4, fontSize: 12, fontStyle: 'italic' }}>
                        →{evaluation.interview_quality_audit.candidate_reverse_questions.what_it_reveals}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 元数据 */}
      <div className="card hint" style={{ fontSize: 13 }}>
        <div>面试 ID:{interview.id}</div>
        <div>状态:{interview.status} · 时长:{interview.duration_minutes} 分钟</div>
        <div>预约时间:{interview.scheduled_at && new Date(interview.scheduled_at).toLocaleString('zh-CN')}</div>
        {interview.started_at && <div>实际开始:{new Date(interview.started_at).toLocaleString('zh-CN')}</div>}
        {interview.ended_at && <div>实际结束:{new Date(interview.ended_at).toLocaleString('zh-CN')}</div>}
        {interview.recording_url && <div>录音:<a href={absoluteUrl(interview.recording_url)} target="_blank" rel="noreferrer">下载</a></div>}
        {evaluation?.created_at && <div>评估生成于:{new Date(evaluation.created_at).toLocaleString('zh-CN')}</div>}
        {Object.keys(interview.usage_tokens).length > 0 && (
          <div>用量:{JSON.stringify(interview.usage_tokens)}</div>
        )}
      </div>
    </div>
  );
}

function statusLabel(s: string): string {
  return ({
    scheduled: '待开始', launching: '启动中', ongoing: '面试中',
    completed: '已完成', failed: '失败', cancelled: '已取消',
  } as Record<string, string>)[s] || s;
}
